/**
 * Brand-kit planning — PURE. No store, no live DOM, no network. Its only
 * imports are the token vocabulary and the other pure halves of this feature
 * (`brand-kit-targets.js`, `section-recolor.js`, `editing/logo-dom.js`), each of
 * which takes its `Document` from an injected `parseHTML`.
 *
 * This module answers "what would applying this kit change?" without changing
 * anything, which is what lets the confirm dialog render a real preview and
 * what makes every decision here testable in plain Node. The impure half (LN-4b)
 * consumes these answers and performs the writes.
 *
 * THE ONE INVARIANT WORTH STATING UP FRONT. `deriveTheme` in `ai/brand-skin.js`
 * applies `isUsableBrandColor` and, when a candidate fails, substitutes the
 * CodeStitch default orange `#ff6a3e`. So a brand-import path that hands it an
 * unusable colour silently produces FOREIGN ORANGE — the worst possible outcome
 * for a feature whose entire promise is "your brand". `electBrandColors`
 * therefore pre-filters with the identical predicate, imported from
 * `theme-tokens.js` rather than re-derived, and a test asserts the substitution
 * can never trigger on this path.
 */

import {
  DEFAULT_THEME,
  THEME_COLOR_KEYS,
  TOKEN_PRIORITY,
  deriveLightTint,
  expandPaletteColors,
  hexToHsl,
  isUsableBrandColor,
  lightenHex,
  normalizeHexColor
} from './theme-tokens.js';
import { iterateAllSections } from './iterate-sections.js';
import { LOGO_SENTINEL_VALUE } from './source-domain-url.js';
import { replaceLogoSentinel } from './logo-sentinel.js';
import { findEmptyOgPages, readVariants } from './brand-kit-targets.js';
import { recolorCss, recolorHtml } from './section-recolor.js';
import { findLogoWrappers, isSourceLockedLogo, setWrapperLogoSource } from '../editing/logo-dom.js';

/** Kit asset keys the builder knows how to place. Mirrors LN-2's allowlist. */
export const KIT_ASSET_KEYS = Object.freeze([
  'logo',
  'logo_transparent',
  'logo_white_bg',
  'logo_dark_bg',
  'favicon_32x32',
  'og_blank'
]);

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/**
 * Election thresholds.
 *
 * `minChroma` exists because a logo palette is dense with anti-aliasing greys;
 * without it the most "dominant" colour is routinely `#6b6b6b`. The distinctness
 * thresholds exist because logo palettes are commonly eight shades of one blue,
 * where "the next entry" is not a secondary colour in any useful sense.
 */
export const ELECTION_DEFAULTS = Object.freeze({
  minChroma: 0.15,
  minHueDelta: 25 / 360,
  minLightnessDelta: 0.25,
  backgroundMinShare: 25,
  backgroundLightAbove: 0.85,
  backgroundDarkBelow: 0.06
});

// ── kit normalisation ──────────────────────────────────────────────────────

/**
 * Validate and canonicalise a kit as it arrives from the Worker.
 *
 * The Worker already normalises; this is the browser-side boundary, and it
 * exists because "the server validated it" stops being true the moment a
 * response is cached, replayed, or hand-edited in a test fixture.
 *
 * @returns {object|null} null when the kit cannot be used at all
 */
export function normalizeBrandKit(kit) {
  const slug = typeof kit?.slug === 'string' ? kit.slug : '';
  if (!SLUG_RE.test(slug)) return null;

  const assets = {};
  for (const entry of Array.isArray(kit?.assets) ? kit.assets : []) {
    const key = entry?.publicKey;
    if (!KIT_ASSET_KEYS.includes(key)) continue;
    assets[key] = {
      publicKey: key,
      filename: typeof entry.filename === 'string' ? entry.filename : '',
      contentType: typeof entry.contentType === 'string' ? entry.contentType : '',
      revision: typeof entry.revision === 'string' ? entry.revision : null
    };
  }

  return {
    slug,
    brandName: typeof kit?.brandName === 'string' ? kit.brandName.slice(0, 200) : slug,
    version: Number.isInteger(kit?.version) ? kit.version : null,
    assetRevision: typeof kit?.assetRevision === 'string' ? kit.assetRevision : null,
    updatedAt: typeof kit?.updatedAt === 'string' ? kit.updatedAt : null,
    palette: normalizeKitPalette(kit?.palette),
    assets
  };
}

function normalizeKitPalette(palette) {
  if (!Array.isArray(palette)) return [];
  const clean = [];
  for (const entry of palette) {
    const hex = normalizeHexColor(entry?.hex)?.toLowerCase();
    if (!hex) continue;
    const percentage = Number(entry?.percentage);
    clean.push({
      hex,
      percentage: Number.isFinite(percentage) && percentage >= 0 ? percentage : null,
      isBackground: entry?.isBackground === true
    });
  }
  return clean;
}

/** Which parts of a brand this kit can actually supply. */
export function kitCapabilities(kit) {
  const has = (key) => Boolean(kit?.assets?.[key]);
  return {
    logo: has('logo') || has('logo_transparent'),
    palette: Array.isArray(kit?.palette) && kit.palette.length > 0,
    favicon: has('logo_transparent') || has('logo_white_bg') || has('logo_dark_bg'),
    ogImage: has('og_blank')
  };
}

// ── colour election ────────────────────────────────────────────────────────

function chromaOf(hex) {
  return hexToHsl(hex)?.s ?? 0;
}

function lightnessOf(hex) {
  return hexToHsl(hex)?.l ?? 0;
}

/** Shortest distance between two hues on the 0–1 circle. */
function hueDistance(a, b) {
  const raw = Math.abs(a - b);
  return Math.min(raw, 1 - raw);
}

/**
 * Reduce a dominance-ordered kit palette to `{primary, secondary}`.
 *
 * The kit gives up to 64 colours; `deriveTheme` wants exactly two. Every step
 * that discards a colour records WHY, so the confirm dialog can say "we skipped
 * your background white and six near-greys" instead of silently doing something
 * the user cannot account for.
 *
 * @returns {{primary:string|null, secondary:string|null, background:string|null, rejections:Array}}
 */
export function electBrandColors(palette, options = {}) {
  const opts = { ...ELECTION_DEFAULTS, ...options };
  const entries = normalizeKitPalette(palette);
  const rejections = [];
  const reject = (hex, reason) => rejections.push({ hex, reason });

  // 1. Background. A 40%-dominant white page background must never become the
  //    brand primary, and it is the single most common palette entry there is.
  let background = null;
  const backgroundIndex = entries.findIndex((entry) => {
    if (entry.isBackground) return true;
    if ((entry.percentage ?? 0) < opts.backgroundMinShare) return false;
    const l = lightnessOf(entry.hex);
    return l > opts.backgroundLightAbove || l < opts.backgroundDarkBelow;
  });
  if (backgroundIndex >= 0) {
    background = entries[backgroundIndex].hex;
    reject(background, 'background');
  }

  // 2 & 3. Usability, then chroma. Order matters only for the rejection
  //    reason the user sees; both are hard filters.
  const survivors = [];
  for (const [index, entry] of entries.entries()) {
    if (index === backgroundIndex) continue;
    if (!isUsableBrandColor(entry.hex)) {
      reject(entry.hex, 'luminance');
      continue;
    }
    if (chromaOf(entry.hex) < opts.minChroma) {
      reject(entry.hex, 'chroma');
      continue;
    }
    survivors.push({ ...entry, index });
  }

  if (!survivors.length) {
    return { primary: null, secondary: null, background, rejections };
  }

  // 4. Primary: most dominant survivor. Ties break by input order, which IS
  //    the dominance order — never by object key order, which is not stable
  //    across a JSON round trip.
  const byDominance = [...survivors].sort((a, b) => {
    const delta = (b.percentage ?? -1) - (a.percentage ?? -1);
    return delta !== 0 ? delta : a.index - b.index;
  });
  const primary = byDominance[0].hex;
  const primaryHsl = hexToHsl(primary);

  // 5. Secondary: the first survivor that is VISUALLY DISTINCT, not merely the
  //    next one. Logo palettes are routinely eight shades of one blue.
  let secondary = null;
  for (const candidate of byDominance.slice(1)) {
    const hsl = hexToHsl(candidate.hex);
    if (!hsl) continue;
    const distinct =
      hueDistance(hsl.h, primaryHsl.h) >= opts.minHueDelta ||
      Math.abs(hsl.l - primaryHsl.l) >= opts.minLightnessDelta;
    if (distinct) {
      secondary = candidate.hex;
      break;
    }
    reject(candidate.hex, 'too-similar');
  }

  // Falling back to the CodeStitch default orange here would reintroduce
  // exactly the foreign colour this whole path exists to remove.
  if (!secondary) secondary = lightenHex(primary, 0.2);

  return { primary, secondary, background, rejections };
}

// ── theme derivation ───────────────────────────────────────────────────────

/**
 * The token map a brand's two colours imply, for an EXISTING project.
 *
 * `expandPaletteColors` writes derived tokens only for keys the project already
 * has — the same semantics `applyPalette` uses — so importing a brand cannot
 * invent tokens a template never declared.
 *
 * `--primaryLight` / `--secondaryLight` use the same `deriveLightTint` that
 * `deriveTheme` uses at composition time, so a kit applied to a fresh project
 * and the same kit applied to an existing one produce identical values — which
 * is why the readability floor has to be shared rather than reimplemented here.
 */
export function deriveBrandTheme(currentTheme, { primary, secondary } = {}) {
  const base = {};
  const bodyText = (currentTheme || DEFAULT_THEME)['--bodyTextColor'] || '#4e4b66';
  if (primary) {
    base['--primary'] = primary;
    base['--primaryLight'] = deriveLightTint(primary, bodyText);
  }
  if (secondary) {
    base['--secondary'] = secondary;
    base['--secondaryLight'] = deriveLightTint(secondary, bodyText);
  }
  if (!Object.keys(base).length) return {};
  return expandPaletteColors(base, { existingTheme: currentTheme || DEFAULT_THEME });
}

/**
 * Map the OUTGOING literal hexes to the incoming ones.
 *
 * Writing `state.theme` repaints only `var()` consumers. Compose-time `skinCSS`
 * already substituted literal hexes into section CSS, so those do not move —
 * which is why a theme write alone leaves a half-branded site. This map is what
 * a section-CSS recolor pass (LN-4b) consumes.
 *
 * THE COLLISION IS REAL, NOT THEORETICAL: in `DEFAULT_THEME`, `--secondary` and
 * `--primaryLight` are both `#ffba43`. One outgoing hex, two incoming ones. The
 * winner is decided by `TOKEN_PRIORITY`, which exists in theme-tokens.js for
 * exactly this, and the loser is reported as a conflict rather than silently
 * dropped.
 *
 * @returns {{map: Map<string,string>, conflicts: Array}}
 */
export function buildThemeColorMap(
  prevTheme = {},
  nextTheme = {},
  { keys = THEME_COLOR_KEYS } = {}
) {
  const byOutgoing = new Map();
  for (const key of keys) {
    const from = normalizeHexColor(prevTheme[key])?.toLowerCase();
    const to = normalizeHexColor(nextTheme[key])?.toLowerCase();
    if (!from || !to || from === to) continue;
    if (!byOutgoing.has(from)) byOutgoing.set(from, []);
    byOutgoing.get(from).push({ key, to });
  }

  const map = new Map();
  const conflicts = [];
  for (const [from, candidates] of byOutgoing) {
    const distinct = new Set(candidates.map((c) => c.to));
    if (distinct.size === 1) {
      map.set(from, candidates[0].to);
      continue;
    }
    const ranked = [...candidates].sort((a, b) => priorityOf(a.key) - priorityOf(b.key));
    map.set(from, ranked[0].to);
    conflicts.push({
      type: 'ambiguous-token-hex',
      hex: from,
      winner: ranked[0].key,
      losers: ranked.slice(1).map((c) => c.key)
    });
  }
  return { map, conflicts };
}

function priorityOf(key) {
  const index = TOKEN_PRIORITY.indexOf(key);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

// ── re-import ──────────────────────────────────────────────────────────────

/**
 * What re-applying a kit should do, given what this project already took.
 *
 * The governing rule for the whole feature, in one line: **we only ever
 * overwrite values we ourselves wrote.** Everything the user has touched since
 * keeps their choice, by default and silently.
 *
 * @returns {{mode:'fresh'|'same'|'assets'|'full', changedParts:string[]}}
 */
export function diffBrandKit(previous, kit) {
  if (!previous || previous.slug !== kit?.slug) {
    return { mode: 'fresh', changedParts: ['logo', 'palette', 'favicon', 'ogImage'] };
  }

  const versionChanged =
    previous.version != null && kit.version != null && previous.version !== kit.version;
  const revisionChanged =
    previous.assetRevision != null &&
    kit.assetRevision != null &&
    previous.assetRevision !== kit.assetRevision;

  if (versionChanged) {
    return { mode: 'full', changedParts: ['logo', 'palette', 'favicon', 'ogImage'] };
  }
  if (revisionChanged) {
    // Bytes moved but the palette did not. Retarget assets by identity; leave
    // the theme alone, because re-deriving it would clobber a hand-edit for no
    // reason.
    return { mode: 'assets', changedParts: ['logo', 'favicon', 'ogImage'] };
  }
  return { mode: 'same', changedParts: [] };
}

/**
 * Has the user hand-edited the theme since we wrote it?
 *
 * Compared against `themeAtApply` — what we actually committed — rather than
 * against a palette id, because a brand import is not one of the named
 * palettes. A divergence is not an error; it decides whether the apply needs an
 * explicit overwrite acknowledgement.
 */
export function themeDivergedSinceApply(currentTheme = {}, themeAtApply = null) {
  if (!themeAtApply || typeof themeAtApply !== 'object') return false;
  return Object.entries(themeAtApply).some(([key, value]) => {
    const current = normalizeHexColor(currentTheme[key])?.toLowerCase();
    const applied = normalizeHexColor(value)?.toLowerCase();
    return Boolean(current) && Boolean(applied) && current !== applied;
  });
}

// ── state.brandKit ─────────────────────────────────────────────────────────

/**
 * The provenance record a project carries.
 *
 * Deliberately in project state rather than a D1 table: it travels with the
 * project, works offline, survives export and duplication, and is what
 * `diffBrandKit` reads to decide a re-import. It is NOT content — see
 * `VERSION_HASH_IGNORED_PATHS`.
 */
export function createBrandKitRecord({
  kit,
  colors,
  themeAtApply,
  assets,
  appliedParts,
  appliedAt,
  logoTargets = [],
  ogPages = []
}) {
  return {
    slug: kit.slug,
    brandName: kit.brandName,
    version: kit.version ?? null,
    assetRevision: kit.assetRevision ?? null,
    kitUpdatedAt: kit.updatedAt ?? null,
    appliedAt: appliedAt ?? null,
    appliedParts: { ...appliedParts },
    colors: { ...colors },
    themeAtApply: { ...themeAtApply },
    assets: { ...assets },
    logoTargets: [...logoTargets],
    ogPages: [...ogPages]
  };
}

/**
 * Repair `state.brandKit` on load. Absent means "never applied", which is a
 * valid state, so this is an invariant normalizer rather than a migration —
 * no `schemaVersion` bump, and an older project simply has no record.
 *
 * @returns {object|null}
 */
export function normalizeBrandKitState(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const slug = typeof value.slug === 'string' ? value.slug : '';
  if (!SLUG_RE.test(slug)) return null;

  const assets = {};
  for (const [key, uri] of Object.entries(value.assets || {})) {
    if (!KIT_ASSET_KEYS.includes(key)) continue;
    // Only pb-asset:// survives. A remote URL here would mean the project is
    // hotlinking a revocable third-party asset, which is the one outcome this
    // whole feature is built to avoid.
    if (typeof uri === 'string' && uri.startsWith('pb-asset://')) assets[key] = uri;
  }

  return {
    slug,
    brandName: typeof value.brandName === 'string' ? value.brandName.slice(0, 200) : slug,
    version: Number.isInteger(value.version) ? value.version : null,
    assetRevision: typeof value.assetRevision === 'string' ? value.assetRevision : null,
    kitUpdatedAt: typeof value.kitUpdatedAt === 'string' ? value.kitUpdatedAt : null,
    appliedAt: Number.isFinite(value.appliedAt) ? value.appliedAt : null,
    appliedParts: {
      logo: value.appliedParts?.logo === true,
      palette: value.appliedParts?.palette === true,
      favicon: value.appliedParts?.favicon === true,
      ogImage: value.appliedParts?.ogImage === true
    },
    colors: {
      primary: normalizeHexColor(value.colors?.primary) || null,
      secondary: normalizeHexColor(value.colors?.secondary) || null
    },
    themeAtApply: normalizeThemeSnapshot(value.themeAtApply),
    assets,
    logoTargets: Array.isArray(value.logoTargets)
      ? value.logoTargets.filter((t) => t && typeof t.eid === 'string').slice(0, 200)
      : [],
    ogPages: Array.isArray(value.ogPages)
      ? value.ogPages.filter((p) => p && typeof p.pageId === 'string').slice(0, 200)
      : []
  };
}

function normalizeThemeSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return {};
  const clean = {};
  for (const [key, value] of Object.entries(snapshot)) {
    const hex = normalizeHexColor(value);
    if (hex && key.startsWith('--')) clean[key] = hex;
  }
  return clean;
}

// ── the whole plan ─────────────────────────────────────────────────────────

/** Which parts a caller can ask for. Every one defaults ON. */
export const ALL_BRAND_PARTS = Object.freeze(['logo', 'palette', 'favicon', 'ogImage']);

/**
 * A placeholder URI used ONLY when planning without real bytes.
 *
 * Preview and apply run the SAME code so the counts a dialog shows cannot drift
 * from what the apply does. The difference is one flag: with no assets the
 * `updates` array is discarded and only the counts survive.
 */
const PREVIEW_URI = 'pb-asset://preview';

function assetFor(assets, key) {
  const entry = assets?.[key];
  if (!entry) return null;
  if (typeof entry === 'string') return { uri: entry };
  return typeof entry.uri === 'string' && entry.uri ? entry : null;
}

/**
 * Which kit asset a wrapper's `variant` slot should receive.
 *
 * `.light` is the mark a template shows on a LIGHT background, so it takes the
 * kit's white-background rendering, and `.dark` takes the dark-background one.
 * The mapping is only ever consulted for a wrapper that already DECLARES two
 * slots — a brand apply must not manufacture a dark variant a template never
 * asked for, because nothing here can know which backgrounds it would sit on.
 */
const VARIANT_ASSET_KEY = Object.freeze({ light: 'logo_white_bg', dark: 'logo_dark_bg' });

/** The single mark used everywhere else: vector first, transparent PNG second. */
export function primaryLogoKey(kit) {
  if (kit?.assets?.logo) return 'logo';
  if (kit?.assets?.logo_transparent) return 'logo_transparent';
  return null;
}

/**
 * Everything applying `kit` to `state` would change.
 *
 * Called with no `assets` it is a PREVIEW: it reports counts, conflicts and the
 * exact set of kit assets that must be downloaded, and returns no updates. The
 * confirm dialog renders that, and the apply then re-runs the identical
 * function with the vendored URIs. One code path, so the dialog cannot promise
 * something the apply does not do.
 *
 * @param {object} state
 * @param {object} rawKit as it arrives from the Worker (or already normalized)
 * @param {object|null} assets `{publicKey: {uri, mime, width, height, aspectRatio}}`
 * @param {object} options
 * @returns {{kit, mode, colors, theme, themeConflicts, recolor, logo, ogPages,
 *   requiredAssets, optionalAssets, updates, summary, conflicts, warnings}}
 */
export function planBrandKitApply(state, rawKit, assets = null, options = {}) {
  const {
    parseHTML,
    parts = ALL_BRAND_PARTS,
    previous = normalizeBrandKitState(state?.brandKit),
    appliedAt = null,
    // The APP ICON is a separate input from the logo, even though both usually
    // come from `logo_transparent`. The caller pre-pads it to a square (a
    // cover-crop at export time would otherwise cut the ends off a wordmark),
    // and that padded square must NOT become the mark placed in the nav — which
    // is exactly what happens if the two share one bag entry on a kit that
    // ships no SVG, because `primaryLogoKey` then falls back to the same key.
    iconAsset = null
  } = options;

  const kit = rawKit?.assets && !Array.isArray(rawKit.assets) ? rawKit : normalizeBrandKit(rawKit);
  const warnings = [];
  const conflicts = [];
  if (!kit) {
    return emptyPlan({ warnings: [{ code: 'kit-unusable' }] });
  }

  const wants = new Set(parts);
  const capabilities = kitCapabilities(kit);
  const mode = diffBrandKit(previous, kit).mode;
  // Preview and apply differ in exactly one thing: whether the bytes exist yet.
  // A preview stands in a placeholder for every asset the KIT declares, runs
  // the identical pass, and throws the updates away — so a count the dialog
  // shows and a change the apply makes cannot come from different code.
  const preview = !assets;
  const bag =
    assets || Object.fromEntries(Object.keys(kit.assets).map((key) => [key, { uri: PREVIEW_URI }]));

  // ── colours ──────────────────────────────────────────────────────────────
  const colors = wants.has('palette')
    ? electBrandColors(kit.palette)
    : { primary: null, secondary: null, background: null, rejections: [] };
  const currentTheme = state?.theme || {};
  const theme = colors.primary ? deriveBrandTheme(currentTheme, colors) : {};
  const { map: recolorMap, conflicts: themeConflicts } = buildThemeColorMap(currentTheme, {
    ...currentTheme,
    ...theme
  });
  conflicts.push(...themeConflicts);
  if (wants.has('palette') && !colors.primary) {
    warnings.push({ code: 'no-usable-brand-color' });
  }
  if (themeDivergedSinceApply(currentTheme, previous?.themeAtApply)) {
    conflicts.push({ type: 'theme-hand-edited' });
  }

  // ── the logo assets this project actually needs ──────────────────────────
  const logoKey = wants.has('logo') ? primaryLogoKey(kit) : null;
  const requiredAssets = [];
  const optionalAssets = [];
  if (logoKey) requiredAssets.push(logoKey);
  if (wants.has('favicon') && kit.assets.logo_transparent) requiredAssets.push('logo_transparent');
  if (wants.has('ogImage') && kit.assets.og_blank) requiredAssets.push('og_blank');

  // ── one pass per section: logo swap, sentinel fill, then recolor ─────────
  const updates = [];
  const logoTargets = [];
  let sourceLocked = 0;
  let sentinelsFilled = 0;
  let recolorSections = 0;
  let recolorNodes = 0;

  const primaryAsset = logoKey ? assetFor(bag, logoKey) : null;

  iterateAllSections(state, (entry) => {
    const basePath = entry.sectionPath.replace(/\.html$/, '');
    let html = entry.html;

    if (primaryAsset && typeof parseHTML === 'function') {
      const swapped = applyLogoToSection(html, {
        parseHTML,
        kit,
        bag,
        primaryAsset,
        preview,
        onTarget: (target) => {
          logoTargets.push({ ...target, sectionPath: entry.sectionPath, kind: entry.kind });
          if (target.sourceLocked) sourceLocked += 1;
          if (target.sourceLocked === false && target.variants.length === 2) {
            for (const variant of target.variants) {
              const key = VARIANT_ASSET_KEY[variant];
              if (key && kit.assets[key] && !optionalAssets.includes(key)) optionalAssets.push(key);
            }
          }
        }
      });
      html = swapped.html;
    }

    if (primaryAsset && html.includes(LOGO_SENTINEL_VALUE)) {
      const filled = replaceLogoSentinel(html, primaryAsset.uri);
      if (filled !== html) {
        sentinelsFilled += 1;
        html = filled;
      }
    }

    if (wants.has('palette') && recolorMap.size) {
      const out = recolorHtml(html, recolorMap, { parseHTML });
      const cssOut = recolorCss(entry.section?.css || '', recolorMap);
      if (out.changed || cssOut.changed) {
        recolorSections += 1;
        recolorNodes += out.changed + cssOut.changed;
      }
      html = out.html;
      if (cssOut.changed) updates.push({ path: `${basePath}.css`, value: cssOut.css });
    }

    if (html !== entry.html) updates.push({ path: entry.sectionPath, value: html });
  });

  // ── theme, app icon, OG ─────────────────────────────────────────────────
  for (const [key, value] of Object.entries(theme)) {
    updates.push({ path: `theme.${key}`, value });
  }

  const icon = wants.has('favicon') ? iconAsset || assetFor(bag, 'logo_transparent') : null;
  if (icon) updates.push({ path: 'pwaInstall.appIcon', value: icon.uri });

  const ogAsset = wants.has('ogImage') ? assetFor(bag, 'og_blank') : null;
  const ogPages = wants.has('ogImage') ? findEmptyOgPages(state) : [];
  if (ogAsset) {
    for (const page of ogPages) updates.push({ path: page.path, value: ogAsset.uri });
  }

  const replaceable = logoTargets.filter((t) => !t.sourceLocked).length;
  const summary = {
    mode,
    logoTargets: logoTargets.length,
    logoReplaced: replaceable,
    sourceLocked,
    sentinelsFilled,
    recolorSections,
    recolorNodes,
    themeTokens: Object.keys(theme).length,
    ogPages: ogAsset ? ogPages.length : 0,
    appIcon: Boolean(icon),
    parts: {
      logo: Boolean(logoKey) && (replaceable > 0 || sentinelsFilled > 0),
      palette: wants.has('palette') && Object.keys(theme).length > 0,
      favicon: Boolean(icon),
      ogImage: Boolean(ogAsset) && ogPages.length > 0
    }
  };

  if (wants.has('logo') && !logoKey) warnings.push({ code: 'kit-has-no-logo' });
  if (wants.has('favicon') && !capabilities.favicon) warnings.push({ code: 'kit-has-no-icon' });
  if (wants.has('ogImage') && !capabilities.ogImage) warnings.push({ code: 'kit-has-no-og' });
  if (sourceLocked) warnings.push({ code: 'logo-source-locked', count: sourceLocked });

  return {
    kit,
    mode,
    colors,
    theme,
    themeConflicts,
    recolor: { map: recolorMap, sectionsAffected: recolorSections, total: recolorNodes },
    logo: { targets: logoTargets, replaceable, sourceLocked, sentinelsFilled },
    ogPages: ogAsset ? ogPages : [],
    requiredAssets: [...new Set(requiredAssets)],
    optionalAssets,
    updates: preview ? [] : updates,
    summary,
    conflicts,
    warnings,
    appliedAt
  };
}

/**
 * Swap every replaceable logo wrapper in one section's HTML.
 *
 * Returns the ORIGINAL string when nothing changed, so the caller can compare
 * by identity and skip a patch — a no-op write still costs a history entry and
 * a collab frame.
 */
function applyLogoToSection(html, { parseHTML, kit, bag, primaryAsset, preview, onTarget }) {
  let doc;
  try {
    doc = parseHTML(`<div id="pb-brand-root">${html}</div>`);
  } catch {
    return { html }; // a section that will not parse is skipped, never guessed at
  }
  const root = doc?.getElementById?.('pb-brand-root');
  if (!root) return { html };

  let changed = 0;
  findLogoWrappers(root).forEach((wrapper, wrapperIndex) => {
    const images = Array.from(wrapper.querySelectorAll('.logo-slot img'));
    const variants = readVariants(images);
    const locked = isSourceLockedLogo(wrapper);
    onTarget?.({
      wrapperIndex,
      eid: wrapper.getAttribute('data-pb-eid') || null,
      sourceLocked: locked,
      variants,
      appliedUri: locked ? null : primaryAsset.uri
    });
    if (locked) return;

    // A template that ships two slots gets two marks; one that ships one gets
    // the single transparent mark. Never the other way round.
    const twoVariant = variants.length === 2;
    const perVariant = twoVariant
      ? variants.map((variant) => ({
          variant,
          asset: assetFor(bag, VARIANT_ASSET_KEY[variant]) || primaryAsset
        }))
      : [{ variant: 'all', asset: primaryAsset }];

    for (const { variant, asset } of perVariant) {
      changed += setWrapperLogoSource(wrapper, {
        variant,
        src: asset.uri,
        alt: `${kit.brandName} logo`,
        assetId: asset.uri.startsWith('pb-asset://') ? asset.uri.slice('pb-asset://'.length) : '',
        mimeType: asset.mime || '',
        width: asset.width,
        height: asset.height,
        aspectRatio: asset.aspectRatio || ''
      });
    }
  });

  if (!changed || preview) return { html };
  return { html: root.innerHTML };
}

function emptyPlan({ warnings = [] } = {}) {
  return {
    kit: null,
    mode: 'fresh',
    colors: { primary: null, secondary: null, background: null, rejections: [] },
    theme: {},
    themeConflicts: [],
    recolor: { map: new Map(), sectionsAffected: 0, total: 0 },
    logo: { targets: [], replaceable: 0, sourceLocked: 0, sentinelsFilled: 0 },
    ogPages: [],
    requiredAssets: [],
    optionalAssets: [],
    updates: [],
    summary: null,
    conflicts: [],
    warnings,
    appliedAt: null
  };
}
